<template>
  <section aria-labelledby="calculator-title" class="calculator">
    <div class="calculator__container">
      <h1 id="calculator-title" class="calculator__title">
        <slot name="title"></slot>
      </h1>

      <p class="calculator__description">
        <slot name="description"></slot>
      </p>

      <div class="calculator__box">
        <form
          id="cmcForm"
          @submit.prevent="performCalculation"
          class="calculator__form"
          :class="{ 
            'calculator__form--withResults': calculationResults,
            'calculator__form--noPopup': !allowPopup
          }"
        >
          <p class="calculator__formTitle">{{ formText.formTitle }}</p>
          <fieldset class="calculator__formItem">
            <label for="frequencySelected">{{
              frequencySelectedField.label
            }}</label>
            <select
              id="frequencySelected"
              v-model.number="formData.frequencySelected"
              :class="[
                'calculator__formInput',
                'calculator__formSelect',
                {
                  'calculator__formInput--error': fieldErrors.frequencySelected,
                  'calculator__formInput--success':
                    !fieldErrors.frequencySelected &&
                    formData.frequencySelected !== 'placeholder',
                  'calculator__formSelect--selected':
                    formData.frequencySelected !== 'placeholder'
                },
              ]"
              @change="
                validateField('frequencySelected', formData.frequencySelected)
              "
              required
            >
              <option
                value="placeholder"
                disabled
                hidden
              >
                10 kHz
              </option>
              <option
                class="calculator__formOption"
                v-for="option in frequencySelectedField.options"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
            <div
              class="calculator__formItem--error"
              :style="{
                display: fieldErrors.frequencySelected ? 'block' : 'none',
              }"
            >
              {{ fieldErrors.frequencySelected }}
            </div>
          </fieldset>

          <fieldset class="calculator__formItem">
            <label for="maximumCurrent">{{ maximumCurrentField.label }}</label>
            <input
              type="number"
              id="maximumCurrent"
              v-model.number="formData.maximumCurrent"
              step="0.01"
              required
              :class="[
                'calculator__formInput',
                {
                  'calculator__formInput--error': fieldErrors.maximumCurrent,
                  'calculator__formInput--success':
                    !fieldErrors.maximumCurrent &&
                    formData.maximumCurrent !== null,
                },
              ]"
              @blur="validateField('maximumCurrent', formData.maximumCurrent)"
              :placeholder="maximumCurrentField.placeholder"
            />
            <div
              class="calculator__formItem--error"
              :style="{
                display: fieldErrors.maximumCurrent ? 'block' : 'none',
              }"
            >
              {{ fieldErrors.maximumCurrent }}
            </div>
          </fieldset>

          <fieldset class="calculator__formItem">
            <label for="currentDensity">{{ currentDensityField.label }}</label>
            <input
              type="number"
              id="currentDensity"
              v-model.number="formData.currentDensity"
              step="0.01"
              required
              :class="[
                'calculator__formInput',
                {
                  'calculator__formInput--error': fieldErrors.currentDensity,
                  'calculator__formInput--success':
                    !fieldErrors.currentDensity &&
                    formData.currentDensity !== null,
                },
              ]"
              @blur="validateField('currentDensity', formData.currentDensity)"
              :placeholder="currentDensityField.placeholder"
            />
            <div
              class="calculator__formItem--error"
              :style="{
                display: fieldErrors.currentDensity ? 'block' : 'none',
              }"
            >
              {{ fieldErrors.currentDensity }}
            </div>
          </fieldset>

          <fieldset class="calculator__formItem">
            <label for="minimumInductance">{{
              minimumInductanceField.label
            }}</label>
            <input
              type="number"
              id="minimumInductance"
              v-model.number="formData.minimumInductance"
              step="0.01"
              required
              :class="[
                'calculator__formInput',
                {
                  'calculator__formInput--error': fieldErrors.minimumInductance,
                  'calculator__formInput--success':
                    !fieldErrors.minimumInductance &&
                    formData.minimumInductance !== null,
                },
              ]"
              @blur="
                validateField('minimumInductance', formData.minimumInductance)
              "
              :placeholder="minimumInductanceField.placeholder"
            />
            <div
              class="calculator__formItem--error"
              :style="{
                display: fieldErrors.minimumInductance ? 'block' : 'none',
              }"
            >
              {{ fieldErrors.minimumInductance }}
            </div>
          </fieldset>

          <div class="calculator__buttons">
            <button
              type="submit"
              :disabled="isLoading || !isFormValid"
              :class="[
                'calculator__calculateButton',
                {
                  'calculator__calculateButton--disabled':
                    isLoading || !isFormValid,
                },
              ]"
            >
              <span v-if="isLoading" class="calculator__spinner"></span>
              {{ formText.calculateButton }}
            </button>
            <button
              type="button"
              @click="clearForm"
              :disabled="isLoading || !isFormValid"
              :class="[
                'calculator__clearButton',
                {
                  'calculator__clearButton--disabled':
                    isLoading || !isFormValid,
                },
              ]"
            >
              {{ formText.clearButton }}
            </button>
          </div>
        </form>

        <CalculatorPlaceholder v-if="!calculationResults">
          <template #title-placeholder>
            <slot name="placeholder-title"></slot>
          </template>
        </CalculatorPlaceholder>

        <div
          v-if="calculationResults && isLargeScreen"
          class="calculator__results"
        >
          <CalculatorResult
            :title="nanoTitle"
            :data="calculationResults.NanoCrystalineData"
            class="calculator__results--nano"
          />
          <CalculatorResult
            :title="ferriteTitle"
            :data="calculationResults.FerriteData"
            class="calculator__results--ferrite"
          />
        </div>

        <CalculatorSavings
          :savingsData="calculationResults.SavingsData"
          :allowPopup="allowPopup"
          :copperPopupTitle="copperPopupTitle"
          :copperDetailsLabel="copperDetailsLabel"
          :enableFab="enableFab"
          :autoOpenOnScroll="autoOpenOnScroll"
          v-if="calculationResults && isLargeScreen"
        >
          <template #title-savings>
            <slot name="savings-title"></slot>
          </template>
          <template #description-savings>
            <slot name="savings-description"></slot>
          </template>
        </CalculatorSavings>
      </div>

      <p class="calculator__observation" v-if="!calculationResults">
        <slot name="observation-text"></slot>
      </p>
    </div>

    <p class="calculator__disclaimer" v-if="!calculationResults">
      <slot name="disclaimer"></slot>
    </p>

    <div v-if="calculationResults || experience">
      <div class="calculator__results" v-if="!isLargeScreen">
        <CalculatorResult
          :title="nanoTitle"
          :data="calculationResults.NanoCrystalineData"
          class="calculator__results--nano"
        />
        <CalculatorResult
          :title="ferriteTitle"
          :data="calculationResults.FerriteData"
          class="calculator__results--ferrite"
        />
      </div>

      <CalculatorSavings
        :savingsData="calculationResults.SavingsData"
        :allowPopup="allowPopup"
        :copperPopupTitle="copperPopupTitle"
        :copperDetailsLabel="copperDetailsLabel"
        :enableFab="enableFab"
        :autoOpenOnScroll="autoOpenOnScroll"
        v-if="!isLargeScreen"
      >
        <template #title-savings>
          <slot name="savings-title"></slot>
        </template>
        <template #description-savings>
          <slot name="savings-description"></slot>
        </template>
      </CalculatorSavings>

      <div class="calculator__downloadContainer">
        <div></div>
        <div class="calculator__download">
          <p class="calculator__downloadTitle">
            <slot name="download-title" v-if="!downloadError"></slot>
            <span v-if="downloadError" class="calculator__downloadError">
              <slot name="download-error" :error="downloadError">{{ downloadError }}</slot>
            </span>
          </p>

          <div class="calculator__downloadButtons">
            <CalculatorDownloadButton
              fileType="pdf"
              :buttonText="downloadButtonText.pdf"
              buttonClass="calculator__downloadButton--pdf"
              @download="(_, done) => downloadResults('pdf', done)"
            >
            </CalculatorDownloadButton>
            <CalculatorDownloadButton
              fileType="csv"
              :buttonText="downloadButtonText.csv"
              buttonClass="calculator__downloadButton--csv"
              @download="(_, done) => downloadResults('csv', done)"
            >
            </CalculatorDownloadButton>
            <CalculatorDownloadButton
              fileType="xlsx"
              :buttonText="downloadButtonText.xlsx"
              buttonClass="calculator__downloadButton--xlsx"
              @download="(_, done) => downloadResults('xlsx', done)"
            >
            </CalculatorDownloadButton>
          </div>
        </div>
      </div>

      <p class="calculator__observation">
        <slot name="observation-text"></slot>
      </p>

      <p class="calculator__disclaimer">
        <slot name="disclaimer"></slot>
      </p>

      <div ref="slotContainer">
        <slot></slot>
      </div>
    </div>
  </section>
</template>

<script>
import CalculatorResult from './calculator-result.vue'
import CalculatorDownloadButton from './calculator-download-button.vue'
import CalculatorSavings from './calculator-savings.vue'
import CalculatorPlaceholder from './calculator-placeholder.vue'

export default {
  name: 'calculator',
  components: {
    CalculatorResult,
    CalculatorDownloadButton,
    CalculatorSavings,
    CalculatorPlaceholder,
  },
  data() {
    return {
      formData: {
        maximumCurrent: null,
        currentDensity: null,
        frequencySelected: 'placeholder',
        minimumInductance: null,
      },
      calculationResults: null,
      isLoading: false,
      apiError: '',
      successMessage: '',
      fieldErrors: {
        maximumCurrent: '',
        currentDensity: '',
        frequencySelected: '',
        minimumInductance: '',
      },
      windowWidth: window.innerWidth,
      downloadError: '',
    }
  },
  props: {
    calculateUrl: {
      type: String,
      required: true,
      default: '/api/sitecore/cmccalculator/calculatecmc',
    },
    exportUrl: {
      type: String,
      required: true,
      default: '/api/sitecore/cmccalculator/export',
    },
    frequencySelectedField: {
      type: Object,
      default: () => ({
        label: 'Frequency selected (10kHz or 100kHz):',
        options: [
          { value: 10, label: '10 kHz' },
          { value: 100, label: '100 kHz' },
        ],
      }),
    },
    maximumCurrentField: {
      type: Object,
      default: () => ({
        label: 'Maximum current (between 10A and 50A):',
        placeholder: '10',
      }),
    },
    currentDensityField: {
      type: Object,
      default: () => ({
        label: 'Current density (between 4 and 8 A/mm²):',
        placeholder: '6',
      }),
    },
    minimumInductanceField: {
      type: Object,
      default: () => ({
        label: 'Minimum inductance (between 0.5 and 30 mH):',
        placeholder: '2.5',
      }),
    },
    formText: {
      type: Object,
      default: () => ({
        formTitle: 'Input data',
        calculateButton: 'Calculate',
        clearButton: 'Clear data',
      }),
    },
    nanoTitle: {
      type: String,
      default: 'Nanocrystaline core',
    },
    ferriteTitle: {
      type: String,
      default: 'Ferrite core',
    },
    downloadButtonText: {
      type: Object,
      default: () => ({
        pdf: 'Download PDF',
        csv: 'Download CSV',
        xlsx: 'Download XLSX',
      }),
    },
    experience: {
      type: Boolean,
      default: false,
    },
    allowPopup: {
      type: Boolean,
      default: true,
    },
    copperPopupTitle: {
      type: String,
      default: 'Copper Price Information',
    },
    copperDetailsLabel: {
      type: String,
      default: 'View complete Copper savings',
    },
    enableFab: {
      type: Boolean,
      default: false,
    },
    autoOpenOnScroll: {
      type: Boolean,
      default: false,
    },
  },
  watch: {
    calculationResults(newValue) {
      if (newValue) {
        this.$nextTick(() => {
          this.pushBannerClickGTM()
        })
      }
    },
  },
  computed: {
    hasError() {
      return this.apiError !== '' || this.errorCount > 0
    },
    errorCount() {
      return Object.values(this.fieldErrors).filter((error) => error !== '')
        .length
    },
    isFormValid() {
      const {
        maximumCurrent,
        currentDensity,
        frequencySelected,
        minimumInductance,
      } = this.formData

      return (
        [10, 100].includes(Number(frequencySelected)) &&
        maximumCurrent >= 10 &&
        maximumCurrent <= 50 &&
        currentDensity >= 4 &&
        currentDensity <= 8 &&
        minimumInductance >= 0.5 &&
        minimumInductance <= 30
      )
    },
    isLargeScreen() {
      return this.windowWidth >= 1200
    },
  },
  methods: {
    performCalculation() {
      this.clearErrors()

      let hasValidationError = false

      if (![10, 100].includes(this.formData.frequencySelected)) {
        this.fieldErrors.frequencySelected =
          'Frequency must be 10kHz or 100kHz'
        return
      }

      if (
        this.formData.maximumCurrent < 10 ||
        this.formData.maximumCurrent > 50
      ) {
        this.fieldErrors.maximumCurrent =
          'Maximum current must be between 10A and 50A'
        hasValidationError = true
      }

      if (
        this.formData.currentDensity < 4 ||
        this.formData.currentDensity > 8
      ) {
        this.fieldErrors.currentDensity =
          'Current density must be between 4 and 8 A/mm²'
        hasValidationError = true
      }

      if (
        this.formData.minimumInductance < 0.5 ||
        this.formData.minimumInductance > 30
      ) {
        this.fieldErrors.minimumInductance =
          'Minimum inductance must be between 0.5 and 30 mH'
        hasValidationError = true
      }

      if (hasValidationError) {
        return
      }

      this.isLoading = true

      fetch(this.calculateUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(this.formData),
      })
        .then((response) => {
          if (!response.ok) {
            return response.text().then((errorText) => {
              throw new Error(
                `Server responded with status: ${response.status} - ${
                  response.statusText
                }. Response body: ${errorText.substring(0, 100)}...`
              )
            })
          }
          return response.json()
        })
        .then((result) => {
          if (result.Success) {
            this.calculationResults = result
            this.successMessage = result.Message
            this.pushGTM()
          } else {
            this.handleValidationErrors(result.Errors)
            this.apiError = result.Message || 'Calculation failed'
          }
        })
        .catch((error) => {
          console.error('Error:', error)
          this.apiError = `<strong>Connection Error:</strong> ${error.message}<br><small>Please check your internet connection or if the server is available.</small>`
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    clearForm() {
      this.formData = {
        maximumCurrent: null,
        currentDensity: null,
        frequencySelected: 'placeholder',
        minimumInductance: null,
      }
      this.calculationResults = null
      this.clearErrors()
      this.pushClearData()
    },
    clearErrors() {
      this.apiError = ''
      this.successMessage = ''
      for (const key in this.fieldErrors) {
        if (Object.prototype.hasOwnProperty.call(this.fieldErrors, key)) {
          this.fieldErrors[key] = ''
        }
      }
    },
    handleValidationErrors(errors) {
      if (errors && Array.isArray(errors)) {
        errors.forEach((error) => {
          const fieldName = this.mapFieldName(error.name)
          if (fieldName) {
            this.$set(this.fieldErrors, fieldName, error.message)
          }
        })
      }
    },
    mapFieldName(apiName) {
      const fieldMapping = {
        FrequencySelected: 'frequencySelected',
        MaximumCurrent: 'maximumCurrent',
        CurrentDensity: 'currentDensity',
        MinimumInductance: 'minimumInductance',
      }
      return fieldMapping[apiName] || null
    },
    downloadResults(fileType, done) {
      if (!this.calculationResults) {
        window.console.log('No calculation results available for download')
        if (done) done()
        return
      }

      this.downloadError = ''
      const requestData = this.removeCommas(this.calculationResults)

      fetch(this.exportUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          data: requestData,
          fileType,
        }),
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
          }
          return response.blob()
        })
        .then((blob) => {
          const url = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', `cmc_results.${fileType}`)

          document.body.appendChild(link)
          link.click()

          document.body.removeChild(link)
          window.URL.revokeObjectURL(url)
          if (done) done()
        })
        .catch((error) => {
          console.error('Download error:', error)
          this.downloadError = error.message
          if (done) done()
        })
    },
    handleResize() {
      this.windowWidth = window.innerWidth
    },
    validateField(fieldName, value) {
      switch (fieldName) {
        case 'frequencySelected':
          if (value === 'placeholder' || ![10, 100].includes(Number(value))) {
            this.fieldErrors.frequencySelected =
              'Frequency must be 10kHz or 100kHz'
            this.pushErrorEvent(this.fieldErrors.frequencySelected, fieldName)
          } else {
            this.fieldErrors.frequencySelected = ''
          }
          break

        case 'maximumCurrent':
          if (value < 10 || value > 50) {
            this.fieldErrors.maximumCurrent =
              'Maximum current must be between 10A and 50A'
            this.pushErrorEvent(this.fieldErrors.maximumCurrent, fieldName)
          } else {
            this.fieldErrors.maximumCurrent = ''
          }
          break

        case 'currentDensity':
          if (value < 4 || value > 8) {
            this.fieldErrors.currentDensity =
              'Current density must be between 4 and 8 A/mm²'
            this.pushErrorEvent(this.fieldErrors.currentDensity, fieldName)
          } else {
            this.fieldErrors.currentDensity = ''
          }
          break

        case 'minimumInductance':
          if (value < 0.5 || value > 30) {
            this.fieldErrors.minimumInductance =
              'Minimum inductance must be between 0.5 and 30 mH'
            this.pushErrorEvent(this.fieldErrors.minimumInductance, fieldName)
          } else {
            this.fieldErrors.minimumInductance = ''
          }
          break

        default:
          break
      }
    },
    pushGTM() {
      window.dataLayer.push({
        event: 'calc_click',
        frequency: this.formData.frequencySelected.toString(),
        current: this.formData.maximumCurrent.toString(),
        density: this.formData.currentDensity.toString(),
        inductance: this.formData.minimumInductance.toString(),
      })
    },
    pushErrorEvent(errorMessage, fieldName) {
      window.dataLayer.push({
        event: 'error',
        name: errorMessage,
        form_field_name: fieldName,
      })
    },
    pushClearData() {
      if (
        this.formText.clearButton &&
        this.formText.clearButton.trim() !== ''
      ) {
        window.dataLayer.push({
          event: 'click',
          label: this.formText.clearButton,
        })
      }
    },
    pushBannerClickGTM() {
      const container = this.$refs.slotContainer
      if (!container) return

      const links = container.querySelectorAll('a')

      if (links.length === 0) return

      links.forEach((link) => {
        link.addEventListener('click', () => {
          const text = link && link.innerText
          const label = text ? text.trim() : ''
          const url = link && link.href ? link.href : ''

          window.dataLayer.push({
            event: 'click',
            label,
            section: 'calculator',
            link_url: url,
          })
        })
      })
    },
    removeCommas(obj) {
      if (Array.isArray(obj)) {
        return obj.map(this.removeCommas)
      } else if (typeof obj === 'object' && obj !== null) {
        const newObj = {}
        for (const key in obj) {
          if (key) {
            newObj[key] = this.removeCommas(obj[key])
          }
        }
        return newObj
      } else if (typeof obj === 'string') {
        return obj.replace(/,/g, '')
      }
      return obj
    },
  },
  mounted() {
    window.addEventListener('resize', this.handleResize)
    this.$nextTick(() => {
      this.pushBannerClickGTM()
    })
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
  },
}
</script>

<style lang="scss" scoped>
.calculator {
  background: #ffffff;
  letter-spacing: 0.01em;

  &__container {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  &__title {
    font-size: 1.5rem;
    font-weight: 700;
    text-align: center;
    color: #454545;
    letter-spacing: 0.01em;

    @media screen and (min-width: 768px) {
      font-size: 2rem;
    }
  }

  &__description {
    font-size: 1rem;
    line-height: 130%;
    text-align: center;
    color: #535353;

    @media screen and (min-width: 1200px) {
      width: 50%;
      align-self: center;
    }
  }

  &__box {
    @media screen and (min-width: 1200px) {
      display: grid;
      grid-template-columns: 1fr 2fr;
      column-gap: 1.625rem;
    }
  }

  &__form,
  &__results {
    border: 0.125rem solid #9ddda9;
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    justify-content: space-between;
  }

  &__form {
    padding: 2rem 1.5rem;

    &--withResults {
      @media screen and (min-width: 1200px) {
        grid-area: 1 / 1 / 3 / 2;
      }
    }
  }

  &__formTitle {
    font-size: 1.125rem;
    font-weight: 700;
    color: #454545;
  }

  &__formItem {
    display: flex;
    flex-direction: column;
    gap: 0.1875rem;
    font-weight: 600;
    font-size: 0.875rem;
    color: #454545;

    &--error {
      color: #d23737;
    }
  }

  &__formSelect {
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    background: transparent;
    background-image: url("data:image/svg+xml;utf8,<svg fill='black' height='24' viewBox='0 0 24 24' width='24' xmlns='http://www.w3.org/2000/svg'><path d='M7 10l5 5 5-5z'/><path d='M0 0h24v24H0z' fill='none'/></svg>");
    background-repeat: no-repeat;
    background-position-x: 95%;
    background-position-y: 0.3125rem;
    color: #6969699e;

    &--selected {
      color: #000000;
    }

    @media screen and (min-width: 768px) {
      background-position-x: 98%;
    }
  }

  &__formInput {
    border: 0.0625rem solid #bcbcbc;
    padding: 0.5625rem 0.875rem;

    &--error {
      border: 0.125rem solid #d23737;
    }

    &--success {
      border: 0.125rem solid #9ddda9;
    }

    &::placeholder {
      color: #6969699e;
    }
  }

  &__formInput:not(&__formSelect)  {
    color: #000000;
  }

  &__formOption {
    color:#000000
  }

  &__results {
    margin-top: 2.5rem;
    padding: 2rem 1.5rem;

    @media screen and (min-width: 768px) {
      display: grid;
      grid-template-columns: 50% 50%;
      justify-items: stretch;
      justify-content: center;
      align-content: space-between;
      gap: 0;
    }

    @media screen and (min-width: 1200px) {
      grid-area: 1 / 2 / 2 / 3;
      margin-top: unset;
    }

    &--nano {
      border-bottom: 0.125rem solid #9ddda9;
      padding-bottom: 2rem;

      @media screen and (min-width: 768px) {
        border-bottom: unset;
        padding-bottom: unset;
        border-right: 0.125rem solid #9ddda9;
      }
    }

    &--ferrite {
      @media screen and (min-width: 768px) {
        padding-left: 1.5rem;
      }
    }
  }

  &__buttons {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  &__calculateButton {
    background-color: #77b883;
    color: #fff;
    transition: all 0.3s ease;

    &:hover:not(&--disabled) {
      background-color: #66a372;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    }

    &:active:not(&--disabled) {
      background-color: #5a9166;
    }

    &:focus-visible {
      outline: 0.125rem solid #454545;
      outline-offset: 0.125rem;
    }

    &--disabled {
      background-color: #a5a5a5;
      cursor: not-allowed;
    }
  }

  &__clearButton {
    border: 0.125rem solid #77b883;
    color: #77b883;
    transition: all 0.3s ease;

    &:hover:not(&--disabled) {
      border-color: #9ddda9;
    }

    &:focus-visible {
      outline: 0.125rem solid #77b883;
      outline-offset: 0.125rem;
    }

    &--disabled {
      border: 0.125rem solid #a5a5a5;
      background-color: #ffffff;
      color: #a5a5a5;
      cursor: not-allowed;
    }
  }

  &__calculateButton,
  &__clearButton {
    font-weight: 700;
    font-size: 0.875rem;
    padding: 0.6rem;
    border-radius: 3.125rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
  }

  &__spinner {
    display: inline-flex;
    width: 1.25rem;
    height: 1.25rem;
    flex-shrink: 0;
    border: 0.125rem solid currentColor;
    border-top: 0.125rem solid transparent;
    border-radius: 50%;
    animation: calculator-spin 1s linear infinite;
  }

  @keyframes calculator-spin {
    100% {
      transform: rotate(360deg);
    }
  }

  &__observation {
    font-size: 0.875rem;
    font-weight: 600;
    color: #454545;
    margin-top: 0.5rem 0;
  }

  &__downloadContainer {
    @media screen and (min-width: 1200px) {
      display: grid;
      justify-content: space-around;
      grid-template-columns: 1fr 2fr;
      justify-items: stretch;
      column-gap: 1.625rem;
    }
  }

  &__download {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
    margin: 2.5rem 0;

    @media screen and (min-width: 768px) {
      gap: 2rem;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
    }
  }

  &__downloadTitle {
    font-weight: 700;
    font-size: 1rem;
    margin-bottom: 0.25rem;
    color: #454545;
    letter-spacing: 0.01em;
    
    @media screen and (min-width: 768px) {
      max-width: 50%;
    }
  }

  &__downloadError {
    font-size: 0.875rem;
    font-weight: 700;
    color: #d23737;
  }

  &__downloadButtons {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;

    @media screen and (min-width: 768px) {
      flex-direction: row;
      gap: 2rem;
    }
  }

  &__disclaimer {
    font-size: 0.75rem;
    line-height: 100%;
    color: #737373;
    line-height: 1rem;
    border-bottom: 0.125rem solid #9ddda9;
    padding-bottom: 2rem;
    margin-bottom: 2rem;
  }
}
</style>
